import { Button } from '@/components/shared/button/Button';
import { TSTitleCard } from '@/components/shared/card/Card';
import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { handleMutationResult } from '@/helpers/toastHelper';
import { Contract } from '@/services/contract/contracts.types';
import { useContracts } from '@/services/contract/useContracts';
import { useUpdateContractMutation } from '@/services/gql/graphql.generated';
import { FunctionComponent, useState } from 'react';
import { IoClose, IoRefresh } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import adminStrings from '../../admin.translations';

interface AdminDeactivateTaskCardProps {
  contract: Contract;
}

const config = {
  active: {
    title: adminStrings.contractsSection.deactivateReactivate.deactivate,
    icon: IoClose,
    info: adminStrings.contractsSection.deactivateReactivate.deactivateInfo,
    buttonText: adminStrings.common.deactivate,
    confirmTitle: adminStrings.contractsSection.deactivateReactivate.confirmTitle,
    confirmBody: adminStrings.contractsSection.deactivateReactivate.confirmDeactivateBody,
    successMessage: adminStrings.contractsSection.deactivateReactivate.successDeactivate,
    errorMessage: adminStrings.contractsSection.deactivateReactivate.errorDeactivate
  },
  inactive: {
    title: adminStrings.contractsSection.deactivateReactivate.reactivate,
    icon: IoRefresh,
    info: adminStrings.contractsSection.deactivateReactivate.reactivateInfo,
    buttonText: adminStrings.common.reactivate,
    confirmTitle: adminStrings.contractsSection.deactivateReactivate.confirmReactivateTitle,
    confirmBody: adminStrings.contractsSection.deactivateReactivate.confirmReactivateBody,
    successMessage: adminStrings.contractsSection.deactivateReactivate.successReactivate,
    errorMessage: adminStrings.contractsSection.deactivateReactivate.errorReactivate
  }
} as const;

const AdminDeactivateReactivateContractCard: FunctionComponent<AdminDeactivateTaskCardProps> = ({ contract }) => {
  const { title, icon, info, buttonText, confirmTitle, confirmBody, successMessage, errorMessage } =
    config[contract.active ? 'active' : 'inactive'];

  const [{ fetching }, update] = useUpdateContractMutation();
  const navigate = useNavigate();

  const { refetch } = useContracts();

  const onSubmit = async () => {
    const { roles, ...inheritableFields } = contract;

    const result = await update({ contract: { ...inheritableFields, active: !contract.active } });

    await refetch({ requestPolicy: 'network-only' });
    setTimeout(
      () =>
        handleMutationResult(result, {
          successMessage,
          errorMessage,
          onSuccess: async () => {
            navigate('/admin/contracts');
          }
        }),
      100
    );
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <TSTitleCard title={title(contract.name ?? '')} icon={icon} className="w-full">
      <div className="flex flex-row items-center space-x-3">
        <Button onClick={() => setIsModalOpen(true)} loading={fetching} danger tint>
          {buttonText}
        </Button>
        <p>{info}</p>
      </div>

      <ConfirmDialog
        onConfirm={onSubmit}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={confirmTitle(contract.name ?? '')}
        confirmLabel={buttonText}
        cancelLabel={adminStrings.common.cancel}
      >
        {confirmBody}
      </ConfirmDialog>
    </TSTitleCard>
  );
};

export default AdminDeactivateReactivateContractCard;
